import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, Percent, Lock, FileText, Plus, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/rental-rates`;
const money = (n) => `$${Number(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const BASES = [['day', 'Daily'], ['week', 'Weekly'], ['month', '4-week']];
export const rentalBasisNoun = (b, n) => ({ day: 'day', week: 'week', month: '4-week period', trip: 'trip' }[b] || 'day') + (Number(n) === 1 ? '' : 's');
const billed = (cost, m) => Math.round(Number(cost || 0) * (1 + Number(m || 0) / 100) * 100) / 100;

/** Rented equipment (Sunbelt sheet): pick the machine, Daily / Weekly / 4-week, how many periods.
 *  Shows our cost and the billed price (cost + margin). Margin defaults to the company setting; super admins can change it. */
export default function RentalRatePicker({ onAdd, testid = 'rent', workOrder = '', date = '' }) {
  const [data, setData] = useState({ rates: [], margin_pct: 30, can_edit_margin: false });
  const [q, setQ] = useState('');
  const [sel, setSel] = useState({ rate_id: '', rate_basis: 'day', qty: 1 });
  const [margin, setMargin] = useState(null); // null = use the default
  const [savingDefault, setSavingDefault] = useState(false);
  const [pos, setPos] = useState({ this_job: [], recent: [], can_create: false });
  const [poSel, setPoSel] = useState('');       // '' = no PO · '__type' = typed · else a PO number from the list
  const [poTyped, setPoTyped] = useState('');
  const [makingPo, setMakingPo] = useState(false);
  useEffect(() => { axios.get(API).then(r => setData(r.data)).catch(() => {}); }, []);
  useEffect(() => { axios.get(`${API}/pos`, { params: { wo: workOrder } }).then(r => setPos(r.data)).catch(() => {}); }, [workOrder]);
  const poNumber = poSel === '__type' ? poTyped.trim() : poSel;

  const rate = data.rates.find(r => r.id === sel.rate_id);
  const m = margin === null || margin === '' ? data.margin_pct : Number(margin);
  const basis = rate?.flat ? 'trip' : sel.rate_basis;
  const cost = rate ? Number(rate.cost[basis] || 0) : 0;
  const price = billed(cost, m);
  const qty = Number(sel.qty) || 0;
  const groups = useMemo(() => {
    const ql = q.trim().toLowerCase();
    const list = ql ? data.rates.filter(r => `${r.category} ${r.name}`.toLowerCase().includes(ql)) : data.rates;
    return list.reduce((acc, r) => { (acc[r.category] = acc[r.category] || []).push(r); return acc; }, {});
  }, [data.rates, q]);

  const saveDefault = async () => {
    setSavingDefault(true);
    try { const r = await axios.put(`${API}/margin`, { margin_pct: m }); setData(d => ({ ...d, margin_pct: r.data.margin_pct })); setMargin(null); toast.success(`Rental margin is now ${r.data.margin_pct}% for everyone`); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    setSavingDefault(false);
  };

  const generatePo = async () => {
    if (!rate || !workOrder) return;
    setMakingPo(true);
    try {
      const r = await axios.post(`${API}/po`, { work_order_number: workOrder, rate_id: rate.id, rate_basis: basis, quantity: qty || 1, date });
      const po = r.data;
      setPos(p => ({ ...p, this_job: [{ po_number: po.po_number, work_order_number: po.work_order_number, description: po.description, date: po.date, amount: po.amount }, ...p.this_job] }));
      setPoSel(po.po_number);
      toast.success(`PO ${po.po_number} raised for ${workOrder} — ${po.company_name}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not raise a PO'); }
    setMakingPo(false);
  };
  const poLabel = (p, mine) => `${p.po_number} · ${mine ? 'this job' : (p.work_order_number || 'no WO')}${p.description ? ` · ${String(p.description).slice(0, 38)}` : ''}${p.date ? ` · ${p.date}` : ''}`;

  const inp = 'bg-white border border-slate-300 rounded-lg text-slate-900 text-sm px-2 py-2 focus:border-blue-500 outline-none';
  return (
    <div className="space-y-3" data-testid={`${testid}-picker`}>
      <div className="flex items-center justify-between gap-2 flex-wrap text-xs text-slate-600">
        <span className="inline-flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-orange-600" /> {data.vendor || 'Sunbelt Rentals'} rate sheet{data.effective_to ? ` · good to ${data.effective_to}` : ''} · our cost, billed at cost + margin</span>
        <span className="inline-flex items-center gap-1.5" data-testid={`${testid}-margin-wrap`}>
          <Percent className="w-3.5 h-3.5" /> Margin
          {data.can_edit_margin ? (
            <>
              <input type="number" min="0" max="500" step="1" value={margin === null ? data.margin_pct : margin} onChange={e => setMargin(e.target.value)} className={`${inp} w-20 text-right font-mono py-1`} data-testid={`${testid}-margin`} />%
              {margin !== null && Number(margin) !== data.margin_pct && (
                <button type="button" onClick={saveDefault} disabled={savingDefault} className="text-[11px] font-bold text-blue-700 hover:underline disabled:opacity-50" data-testid={`${testid}-margin-save`}>set as default</button>
              )}
            </>
          ) : (
            <span className="font-mono font-bold text-slate-900 inline-flex items-center gap-1" title="Set by a super admin">{data.margin_pct}% <Lock className="w-3 h-3 text-slate-400" /></span>
          )}
        </span>
      </div>
      <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter rentals… (scissor, telehandler, 60', forklift)" className={`${inp} w-full`} data-testid={`${testid}-filter`} />
      <select value={sel.rate_id} onChange={e => setSel({ ...sel, rate_id: e.target.value })} className={`${inp} w-full`} data-testid={`${testid}-select`}>
        <option value="">Select rented equipment…</option>
        {Object.entries(groups).map(([cat, items]) => (
          <optgroup key={cat} label={cat}>
            {items.map(r => <option key={r.id} value={r.id}>{r.name} — {r.flat ? `${money(r.cost.trip)} each way` : `${money(r.cost.day)}/day · ${money(r.cost.week)}/wk · ${money(r.cost.month)}/4wk`} (cost)</option>)}
          </optgroup>
        ))}
      </select>
      <div className="flex gap-2 items-end flex-wrap">
        {!rate?.flat && (
          <div className="flex rounded-lg overflow-hidden border border-slate-300" data-testid={`${testid}-basis`}>
            {BASES.map(([b, lbl]) => (
              <button key={b} type="button" onClick={() => setSel({ ...sel, rate_basis: b })}
                className={`px-3 py-2 text-xs font-semibold ${sel.rate_basis === b ? 'bg-orange-600 text-white' : 'bg-white text-slate-700 hover:bg-slate-100'}`} data-testid={`${testid}-basis-${b}`}>
                {lbl}{rate ? <span className="block text-[10px] font-normal opacity-80">{money(billed(rate.cost[b], m))}</span> : null}
              </button>
            ))}
          </div>
        )}
        <div>
          <label className="text-[11px] text-slate-500">How many {rentalBasisNoun(basis, 2)}</label>
          <input type="number" min="0.5" step="0.5" value={sel.qty} onChange={e => setSel({ ...sel, qty: e.target.value })} className={`${inp} w-24 text-right font-mono`} data-testid={`${testid}-qty`} />
        </div>
        <div className="flex-1 min-w-[200px] text-right">
          <p className="text-[11px] text-slate-500">Cost {rate ? money(cost) : '—'} → billed {rate ? money(price) : '—'} per {rentalBasisNoun(basis, 1)}</p>
          <p className="font-mono font-bold text-slate-900" data-testid={`${testid}-total`}>{rate ? money(price * qty) : '—'}{rate && <span className="text-[11px] font-sans font-normal text-emerald-700 ml-1">(+{money((price - cost) * qty)} margin)</span>}</p>
        </div>
        <button type="button" disabled={!rate || !(qty > 0)}
          onClick={() => { onAdd({ rate, rate_id: rate.id, rate_basis: basis, qty, unit_price: price, cost_rate: cost, margin_pct: m, margin_override: margin !== null && Number(margin) !== data.margin_pct, name: `${rate.vendor} — ${rate.name}`, po_number: poNumber || null }); setSel({ rate_id: '', rate_basis: sel.rate_basis, qty: 1 }); }}
          className="px-4 py-2 rounded-lg bg-orange-600 hover:bg-orange-500 disabled:opacity-40 text-white text-sm font-semibold inline-flex items-center gap-1" data-testid={`${testid}-add`}>
          <Building2 className="w-4 h-4" /> Add
        </button>
      </div>
      <div className="flex gap-2 items-center flex-wrap rounded-lg border border-slate-200 bg-slate-50 px-3 py-2" data-testid={`${testid}-po-wrap`}>
        <FileText className="w-3.5 h-3.5 text-slate-500" />
        <span className="text-xs font-semibold text-slate-700">Sunbelt PO</span>
        <select value={poSel} onChange={e => setPoSel(e.target.value)} className={`${inp} py-1.5 max-w-[22rem]`} data-testid={`${testid}-po-select`}>
          <option value="">No PO yet</option>
          {pos.this_job.length > 0 && <optgroup label={`This job (${workOrder})`}>{pos.this_job.map(p => <option key={p.po_number} value={p.po_number}>{poLabel(p, true)}</option>)}</optgroup>}
          {pos.recent.length > 0 && <optgroup label="Recent Sunbelt POs">{pos.recent.map(p => <option key={p.po_number} value={p.po_number}>{poLabel(p, false)}</option>)}</optgroup>}
          <option value="__type">Type a PO #…</option>
        </select>
        {poSel === '__type' && (
          <input value={poTyped} onChange={e => setPoTyped(e.target.value)} placeholder="PO number" className={`${inp} py-1.5 w-28 font-mono`} data-testid={`${testid}-po-typed`} autoFocus />
        )}
        {pos.can_create && (
          <button type="button" onClick={generatePo} disabled={!rate || makingPo || !workOrder}
            title={rate ? `Raise a new Sunbelt PO for ${workOrder} with this rental on it` : 'Pick the rental first'}
            className="px-3 py-1.5 rounded-lg border border-blue-300 bg-white text-blue-700 hover:bg-blue-50 disabled:opacity-40 text-xs font-semibold inline-flex items-center gap-1" data-testid={`${testid}-po-generate`}>
            {makingPo ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Generate a new PO
          </button>
        )}
        {poNumber && <span className="text-xs text-emerald-700 font-mono font-bold" data-testid={`${testid}-po-chosen`}>→ PO {poNumber}</span>}
      </div>
      {rate?.note && <p className="text-[11px] text-slate-500">{rate.note}</p>}
      <p className="text-[11px] text-slate-500">Hour-metered machines: double shift (9–16 hrs) = rate × 1.5, triple (17–24 hrs) = rate × 2 — bump the quantity to match.</p>
    </div>
  );
}
